/**
 * TcStringEncoder — Encodes a TC String per IAB TCF 2.2 specification.
 *
 * The TC string is a base64url-encoded bitfield with the Core segment.
 * Reference: https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20Consent%20string%20and%20vendor%20list%20formats%20v2.md
 */

// TCF Created/LastUpdated are "epoch deciseconds" — the UNIX epoch (1970),
// not the TCF policy launch date. Encoding from a 2020 base made every
// spec-conformant decoder (verified against @iabtcf/core) report timestamps
// 50 years in the past. 36 bits of Unix-epoch deciseconds is ample (~2187).
const TCF_EPOCH_MS = 0;

// Base64url alphabet (no padding)
const B64_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

export interface TcStringParams {
  cmpId: number;
  cmpVersion: number;
  consentLanguage: string; // e.g. "EN"
  vendorListVersion: number;
  purposeConsents: Set<number>; // purpose IDs 1-24
  purposeLegitimateInterests: Set<number>;
  specialFeatureOptins: Set<number>; // 1-12
  vendorConsents: Set<number>; // vendor IDs
  vendorLegitimateInterests: Set<number>;
  publisherCC: string; // e.g. "US"
}

/**
 * BitWriter — Writes individual bits into a buffer, then encodes to base64url.
 */
class BitWriter {
  private bits: number[] = [];

  /** Write `length` bits from `value` (MSB first) */
  writeInt(value: number, length: number): void {
    // Extract bits by division rather than `value >>> i`: JS bitwise operators
    // are 32-bit, so a shift of 32 or more wraps (masked mod 32), corrupting the
    // 36-bit Created / LastUpdated timestamp fields. Division is exact for the
    // integer values used here (all well within 2^53).
    const v = Math.max(0, Math.trunc(value));
    for (let i = length - 1; i >= 0; i--) {
      this.bits.push(Math.floor(v / Math.pow(2, i)) % 2);
    }
  }

  /** Write a boolean as 1 bit */
  writeBool(value: boolean): void {
    this.bits.push(value ? 1 : 0);
  }

  /** Write a set of IDs as a bitfield of `length` bits (1-indexed) */
  writeBitfield(ids: Set<number>, length: number): void {
    for (let i = 1; i <= length; i++) {
      this.bits.push(ids.has(i) ? 1 : 0);
    }
  }

  /** Encode a 2-char language/country string (each char = 6 bits, offset from 'A' = 0) */
  writeLetters(str: string): void {
    const upper = str.toUpperCase();
    this.writeInt(upper.charCodeAt(0) - 65, 6); // 'A' = 0
    this.writeInt(upper.charCodeAt(1) - 65, 6);
  }

  /** Convert bit array to base64url string (no padding) */
  toBase64Url(): string {
    // Pad to multiple of 6 bits
    while (this.bits.length % 6 !== 0) {
      this.bits.push(0);
    }

    let result = "";
    for (let i = 0; i < this.bits.length; i += 6) {
      let val = 0;
      for (let j = 0; j < 6; j++) {
        val = (val << 1) | (this.bits[i + j] || 0);
      }
      result += B64_CHARS[val];
    }
    return result;
  }
}

export class TcStringEncoder {
  /**
   * Encode consent choices into a TCF 2.2 TC string (Core segment only).
   */
  encode(params: TcStringParams): string {
    const w = new BitWriter();
    const deciseconds = Math.round((Date.now() - TCF_EPOCH_MS) / 100);

    // --- Core Segment ---

    // Version: 6 bits = 2 (TCF v2)
    w.writeInt(2, 6);

    // Created: 36 bits (deciseconds since TCF epoch)
    w.writeInt(deciseconds, 36);

    // LastUpdated: 36 bits
    w.writeInt(deciseconds, 36);

    // CmpId: 12 bits
    w.writeInt(params.cmpId, 12);

    // CmpVersion: 12 bits
    w.writeInt(params.cmpVersion, 12);

    // ConsentScreen: 6 bits (1 = first screen)
    w.writeInt(1, 6);

    // ConsentLanguage: 12 bits (2 letters)
    w.writeLetters(params.consentLanguage);

    // VendorListVersion: 12 bits
    w.writeInt(params.vendorListVersion, 12);

    // TcfPolicyVersion: 6 bits = 5 (TCF 2.2)
    w.writeInt(5, 6);

    // IsServiceSpecific: 1 bit = 1
    w.writeBool(true);

    // UseNonStandardTexts: 1 bit = 0
    w.writeBool(false);

    // SpecialFeatureOptins: 12 bits (IDs 1-12)
    w.writeBitfield(params.specialFeatureOptins, 12);

    // PurposeConsents: 24 bits (IDs 1-24)
    w.writeBitfield(params.purposeConsents, 24);

    // PurposeLegitimateInterests: 24 bits (IDs 1-24)
    w.writeBitfield(params.purposeLegitimateInterests, 24);

    // PurposeOneTreatment: 1 bit = 0
    w.writeBool(false);

    // PublisherCC: 12 bits (2 letters)
    w.writeLetters(params.publisherCC);

    // --- Vendor Consent Section ---
    const maxVendorConsent = maxId(params.vendorConsents);
    w.writeInt(maxVendorConsent, 16);
    // IsRangeEncoding: 1 bit = 0 (bitfield)
    w.writeBool(false);
    // Vendor consent bitfield
    if (maxVendorConsent > 0) {
      w.writeBitfield(params.vendorConsents, maxVendorConsent);
    }

    // --- Vendor Legitimate Interest Section ---
    const maxVendorLI = maxId(params.vendorLegitimateInterests);
    w.writeInt(maxVendorLI, 16);
    // IsRangeEncoding: 1 bit = 0 (bitfield)
    w.writeBool(false);
    // Vendor LI bitfield
    if (maxVendorLI > 0) {
      w.writeBitfield(params.vendorLegitimateInterests, maxVendorLI);
    }

    // --- Publisher Restrictions Section ---
    // NumPubRestrictions: 12 bits. Mandatory field that closes the core
    // segment; 0 = no publisher restrictions. Omitting it made spec-conformant
    // decoders read past the vendor LI bitfield and misparse the string.
    w.writeInt(0, 12);

    return w.toBase64Url();
  }
}

/** Get the maximum value in a set, or 0 if empty */
function maxId(set: Set<number>): number {
  let max = 0;
  set.forEach((id) => {
    if (id > max) max = id;
  });
  return max;
}
